import {
  getStaticToken,
  getPropertyInfo,
} from '../Constants/api';
import {
  clearStoredPropertyContext,
  createPropertyError,
  getStoredVisitId,
  logPropertyResolutionFailure,
} from '../utils/propertyRecovery';
import { isNumericId } from '../utils';
import { PropertyError, PropertyInfo, PropertyRequestContext, TourArrivalSource } from '../types/property';

type PropertyLoadResult = {
  property: PropertyInfo;
  requestContext: PropertyRequestContext;
};

type LoadPropertyContextArgs = {
  propertyId: string | number;
  builderId?: number;
  customerId?: string | number;
  arrivalSource: TourArrivalSource;
  forceRehydrate?: boolean;
};

const MAX_ATTEMPTS = 2;

const getPropertyId = (propertyId: string | number): string => {
  if (!isNumericId(propertyId)) {
    throw createPropertyError({
      code: 'PROPERTY_ID_INVALID',
      message: `Property identifier "${propertyId}" is missing or malformed`,
    });
  }

  return String(propertyId).trim();
};

const fetchPropertyContext = async (
  args: LoadPropertyContextArgs,
  propertyId: string,
  requestContext: PropertyRequestContext,
  refreshToken: boolean,
): Promise<PropertyLoadResult> => {
  await getStaticToken(refreshToken, requestContext);

  const property: PropertyInfo = await getPropertyInfo(
    propertyId,
    args.customerId,
    requestContext,
  );

  return { property, requestContext };
};

export const loadPropertyContext = async (
  args: LoadPropertyContextArgs,
): Promise<PropertyLoadResult> => {
  const requestContext: PropertyRequestContext = {
    arrivalSource: args.arrivalSource,
  };
  const propertyId = getPropertyId(args.propertyId);
  const previousVisitId = getStoredVisitId();

  const maxAttempts = args.forceRehydrate ? 1 : MAX_ATTEMPTS;

  if (args.forceRehydrate) {
    clearStoredPropertyContext();
  }

  let lastError: PropertyError | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await fetchPropertyContext(
        args,
        propertyId,
        requestContext,
        Boolean(args.forceRehydrate || attempt > 1),
      );
    } catch (error) {
      lastError = createPropertyError(error);

      logPropertyResolutionFailure({
        error: lastError,
        propertyId,
        builderId: args.builderId,
        customerId: args.customerId,
        visitId: previousVisitId,
        arrivalSource: args.arrivalSource,
        attempt,
      });

      if (!lastError.retryable) break;

      clearStoredPropertyContext();
    }
  }

  throw lastError;
};
